import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveHostContext } from "@/lib/lp/host";
import {
  DEFAULT_LANG,
  SUPPORTED_LANGS,
  SupportedLang,
  getLangFromPath,
  stripLangFromPath,
  withLang,
} from "./config";

function getSafeStoredLang(): SupportedLang {
  const stored = localStorage.getItem("lang");
  return SUPPORTED_LANGS.includes(stored as SupportedLang) ? (stored as SupportedLang) : DEFAULT_LANG;
}

const AUTH_PATH_ALIASES: Record<string, "signup" | "signin"> = {
  "/signup": "signup",
  "/sign-up": "signup",
  "/register": "signup",
  "/join": "signup",
  "/signin": "signin",
  "/sign-in": "signin",
};

const PRIVATE_ROUTE_PREFIXES = [
  "/login",
  "/signup",
  "/auth/",
  "/dashboard",
  "/seller/",
  "/admin/",
  "/supplier/",
  "/support-console/",
  "/investor/",
  "/call-center/",
  "/logistics/",
  "/account-manager/",
];

function isPrivateRoute(pathname: string) {
  const rest = stripLangFromPath(pathname) || "/";
  return PRIVATE_ROUTE_PREFIXES.some((prefix) =>
    prefix.endsWith("/") ? rest.startsWith(prefix) : rest === prefix || rest.startsWith(`${prefix}/`),
  );
}

function setRobotsMeta(content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "robots";
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function normalizeAuthPath(restPath: string): { path: string; mode?: "signup" | "signin" } {
  const key = restPath.length > 1 && restPath.endsWith("/") ? restPath.slice(0, -1) : restPath;
  const mode = AUTH_PATH_ALIASES[key.toLowerCase()];
  if (mode) return { path: "/login", mode };
  return { path: restPath };
}

function mergeMode(search: string, mode?: "signup" | "signin"): string {
  const params = new URLSearchParams(search);
  if (mode && !params.get("mode")) params.set("mode", mode);
  const str = params.toString();
  return str ? `?${str}` : "";
}

/**
 * Ensures application URLs carry a language prefix (/fr, /ar or /en),
 * while preserving standalone public landing-page URLs,
 * syncs i18next + <html lang/dir>, and prevents auth/private application
 * routes from being indexed by search engines.
 */
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setRobotsMeta(
      isPrivateRoute(location.pathname)
        ? "noindex,nofollow,noarchive"
        : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    );

    const seg = location.pathname.split("/")[1];
    const hasLang = SUPPORTED_LANGS.includes(seg as SupportedLang);
    const host = resolveHostContext();
    const publicLpPath = /^\/lp\/[^/]+\/?$/.test(stripLangFromPath(location.pathname));

    // LP URLs are standalone: adding an app language prefix sends them to
    // the localized catch-all. Store/custom hosts also own their slug paths.
    if (host.kind !== "app" || publicLpPath) {
      const lang = host.kind === "app" && hasLang ? (seg as SupportedLang) : getSafeStoredLang();
      if (i18n.language !== lang) void i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
      if (host.kind === "app" && hasLang) {
        navigate(stripLangFromPath(location.pathname) + location.search + location.hash, { replace: true });
      }
      return;
    }

    if (location.pathname === "/") {
      const lang = getSafeStoredLang();
      if (i18n.language !== lang) void i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
      return;
    }

    if (!hasLang) {
      const lang = getSafeStoredLang();
      const { path, mode } = normalizeAuthPath(location.pathname);
      const target = withLang(lang, path) + mergeMode(location.search, mode) + location.hash;
      navigate(target, { replace: true });
      return;
    }

    const lang = seg as SupportedLang;
    if (i18n.language !== lang) void i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";

    const rest = stripLangFromPath(location.pathname) || "/";
    const { path: normalized, mode } = normalizeAuthPath(rest);
    if (normalized !== rest) {
      const target = withLang(lang, normalized) + mergeMode(location.search, mode) + location.hash;
      navigate(target, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, i18n, navigate]);

  return <>{children}</>;
}

export function useLang() {
  const location = useLocation();
  const navigate = useNavigate();
  const current = getLangFromPath(location.pathname);

  const setLang = (lang: SupportedLang) => {
    const rest = stripLangFromPath(location.pathname);
    navigate(withLang(lang, rest) + location.search + location.hash);
  };

  const lp = (path: string) => {
    const [rawPath, rawQuery = ""] = path.split("?");
    const [query, hash = ""] = rawQuery.split("#");
    const { path: normalized, mode } = normalizeAuthPath(rawPath);
    const search = mergeMode(query ? `?${query}` : "", mode);
    const hashPart = hash ? `#${hash}` : "";
    return withLang(current, normalized) + search + hashPart;
  };

  return { lang: current, setLang, lp };
}
